import React from "react";
import Highlighter from "react-highlight-words";
import { useTranslation } from "react-i18next";
import UserSettingsService from "src/services/userSettingsService";
import { HoverRow } from "src/ui/shared/table/HoverRow";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "src/ui/shared/table/MinimalTableComponents";
import { fuzzyFilter } from "src/ui/shared/table/Tanstack.utils";
import { Tooltip } from "src/ui/shared/Tooltip";
import { makeStyles } from "tss-react/mui";

import { useAllCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { FileContentType } from "@dashboard/files/types";
import { PaneElement } from "@dashboard/shared/components/SidePanel/shared/PaneElement";
import { Device, DeviceState } from "@devices/types";
import { Box, Paper, TableCell, TableContainer, TableRow, Theme, Typography, useTheme } from "@mui/material";
import { QueryBuilderField } from "@shared/form/QueryBuilderField/QueryBuilderField";
import { FilterHeader } from "@shared/table/headers/FilterHeader";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import {
    CellContext,
    ColumnDef,
    ColumnFiltersState,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { ActionCell } from "./ActionCell";
import { NameCell } from "./NameCell";

const useStyles = makeStyles()((theme: Theme) => ({
    content: { maxHeight: "100%", marginBottom: theme.spacing(7) },
    buttonBox: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: theme.spacing(1),
        width: 140,
        height: 50,
    },
    box: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        cursor: "pointer",
        "& i": {
            padding: theme.spacing(1),
            width: theme.spacing(2),
            height: theme.spacing(2),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
    },
    expandedBox: { paddingLeft: theme.spacing(5), display: "flex", gap: theme.spacing(1) },
    searchBox: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
    },
}));

export type Application = {
    enabled: boolean;
    label: string;
    launchable: boolean;
    package: string;
    running: boolean;
    system: "system" | "application";
    versionCode: number;
    versionName: string;
};

const StatusCell = (props: CellContext<Application, unknown>) => {
    return (
        <Box style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {props.row.original.running && (
                <Tooltip placement="top" title="Application is running">
                    <i className="fa-regular fa-circle-play" />
                </Tooltip>
            )}
            {!props.row.original.enabled && (
                <Tooltip placement="top" title="Application is hidden">
                    <i className="fa-sharp fa-solid fa-eye-slash" />
                </Tooltip>
            )}
        </Box>
    );
};
const TypeCell = (props: CellContext<Application, unknown>) => {
    const { t } = useTranslation(["sidepanels"]);
    return (
        <Box>
            {props.getValue<string>() === "system" ? (
                <Tooltip title={t("sidepanels:devicePanel.tooltip.systemApp")}>
                    <i className="fa-solid fa-screwdriver-wrench" />
                </Tooltip>
            ) : (
                <Tooltip title={t("sidepanels:devicePanel.tooltip.installedApp")}>
                    <i className="fa-solid fa-user" />
                </Tooltip>
            )}
        </Box>
    );
};
const transformData = (data?: DeviceState["packages"]): Application[] => {
    if (!data) {
        return [];
    }
    return data.map((item) => ({
        ...item,
        system: item.system ? "system" : "application",
    }));
};

interface Props {
    device: Device;
    groupId: number;
}
const AppTab = (props: Props) => {
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        UserSettingsService.getLicenseSidePanelAppsTabSystemFilter(),
    );

    const [expanded, setExpanded] = React.useState<ExpandedState>({});
    const [searchQuery, setSearchQuery] = React.useState("");

    const { classes } = useStyles();
    const theme = useTheme();
    const { t } = useTranslation(["sidepanels", "common"]);

    const data = React.useMemo(() => transformData(props.device.lastState?.packages), [props.device.lastState?.packages]);

    const { data: apks } = useAllCurrentWorkspaceFiles({
        groups: [props.groupId],
        contentTypes: [FileContentType.Apk, FileContentType.ApkV2],
    });
    const state = React.useMemo(
        () => ({ expanded, globalFilter: searchQuery, columnFilters }),
        [expanded, searchQuery, columnFilters],
    );
    const tableColumns: ColumnDef<Application, unknown>[] = React.useMemo(
        () => [
            {
                header: t("common:label.name"),
                accessorKey: "label",
                cell: (info) => <NameCell {...info} searchWords={[searchQuery]} />,
                enableSorting: true,
            },
            {
                header: t("common:label.status"),
                accessorKey: "status",
                cell: (info) => <StatusCell {...info} />,
                enableSorting: false,
            },
            {
                header: t("common:label.type"),
                accessorKey: "system",
                cell: (info) => <TypeCell {...info} />,
                enableSorting: true,
                filterFn: "arrIncludesSome",
            },
            {
                header: t("common:label.version"),
                accessorKey: "versionCode",
                cell: (info) => (
                    <Highlighter
                        searchWords={[searchQuery]}
                        textToHighlight={String(info.getValue<number>())}
                        autoEscape
                        unhighlightStyle={{ color: theme.palette.emteria.text.primary }}
                    />
                ),
                enableSorting: true,
            },
            {
                accessorKey: "action",
                header: "",
                cell: (info) => <ActionCell device={props.device} apks={apks} {...info} />,
            },
        ],
        [searchQuery, props.device, apks, t],
    );
    const table = useReactTable<Application>({
        data,
        state,
        initialState: {
            sorting: [
                { id: "system", desc: false },
                { id: "label", desc: false },
            ],
        },
        columns: tableColumns,
        filterFns: { fuzzy: fuzzyFilter },
        getRowCanExpand: () => true,
        onExpandedChange: setExpanded,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnFiltersChange: setColumnFilters,
    });

    const handleResetFilter = () => {
        setColumnFilters([]);
        setSearchQuery("");
        table.setPageIndex(0);
    };

    React.useEffect(() => {
        UserSettingsService.setWorkspaceValue("licenseSidePanel.appsTab.columnFilters", columnFilters);
    }, [columnFilters]);

    return (
        <PaneElement.Container>
            <Box className={classes.content}>
                <PaneElement.Header>{t("sidepanels:devicePanel.appsTab.availableApplications")}</PaneElement.Header>
                <Box className={classes.searchBox}>
                    <QueryBuilderField
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onClear={() => setSearchQuery("")}
                        placeholder={t("common:button.search")}
                        name="app-search"
                        autoComplete="app-search-field"
                    />
                </Box>

                <TableContainer component={Paper}>
                    <MinimalTable>
                        <MinimalTableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        if (header.id === "system") {
                                            const systemValue =
                                                (columnFilters.find((f) => f.id === "system")?.value as string[] | undefined) ??
                                                [];
                                            return (
                                                <Header key={header.id} {...header}>
                                                    <FilterHeader
                                                        title={t("sidepanels:devicePanel.table.filter.applications")}
                                                        sortDisabled
                                                        options={[
                                                            {
                                                                key: "system",
                                                                label: t("sidepanels:devicePanel.options.system"),
                                                            },
                                                            {
                                                                key: "application",
                                                                label: t("sidepanels:devicePanel.options.application"),
                                                            },
                                                        ]}
                                                        initialValues={{
                                                            system: systemValue.includes("system"),
                                                            application: systemValue.includes("application"),
                                                        }}
                                                        onSubmit={(values) => {
                                                            const value = Object.keys(values).filter((k) => values[k]);
                                                            setColumnFilters([{ id: "system", value }]);
                                                        }}
                                                    />
                                                </Header>
                                            );
                                        }

                                        return <Header key={header.id} {...header} />;
                                    })}
                                </TableRow>
                            ))}
                        </MinimalTableHeader>
                        <TableBody
                            table={table}
                            filter={{ isActive: !!columnFilters.length || !!searchQuery, onResetFilter: handleResetFilter }}
                            noDataMessage={t("sidepanels:devicePanel.table.noApplications")}
                        >
                            {table.getRowModel().rows.map((row) => (
                                <React.Fragment key={row.id}>
                                    <HoverRow {...row}>
                                        {row.getVisibleCells().map((cell) => (
                                            <MinimalTableCell key={cell.id} onClick={() => null}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </MinimalTableCell>
                                        ))}
                                    </HoverRow>

                                    {row.getIsExpanded() ? (
                                        <TableRow>
                                            <TableCell colSpan={row.getAllCells().length} style={{ borderBottom: "none" }}>
                                                <Box className={classes.expandedBox}>
                                                    <Box style={{ display: "flex", flexDirection: "column" }}>
                                                        <Typography variant="body1" fontWeight={600}>
                                                            {t("sidepanels:devicePanel.appsTab.packageName")}
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight={600}>
                                                            {t("sidepanels:devicePanel.appsTab.versionName")}
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight={600}>
                                                            {t("sidepanels:devicePanel.appsTab.versionCode")}
                                                        </Typography>
                                                    </Box>
                                                    <Box style={{ display: "flex", flexDirection: "column" }}>
                                                        <Typography variant="body1">{row.original.package}</Typography>
                                                        <Typography variant="body1">{row.original.versionName}</Typography>
                                                        <Typography variant="body1">{row.original.versionCode}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </TableContainer>
            </Box>
        </PaneElement.Container>
    );
};

export default AppTab;
